import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import useLanguageSync from '../hooks/useLanguageSync';
import SessionSidebar from './SessionSidebar';
import SidebarLanguageSelector from './SidebarLanguageSelector';
import ConfirmDeleteAllModal from './ConfirmDeleteAllModal';
import { BETA_MODE, DEMO_MODE_AVAILABLE } from '../config/featureFlags';

function BetaPill() {
  return (
    <span
      className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded
        bg-uel-purple-100 text-uel-purple-800
        dark:bg-uel-purple-900/40 dark:text-uel-purple-200"
      title="PAILA is in beta — your feedback shapes what we build next."
    >
      BETA
    </span>
  );
}

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { t } = useTranslation('common');
  const { user, signOut, idToken, isAdmin, impersonatedStudentId, setImpersonation } = useAuth();
  const { theme, setTheme, effectiveTheme } = useTheme();
  const { demoMode, setDemoMode } = useDemoMode();
  useLanguageSync();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [impersonateInput, setImpersonateInput] = useState('');
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

  // Pre-fill default student ID once admin status resolves (async after mount)
  useEffect(() => {
    if (isAdmin && !impersonateInput && !impersonatedStudentId) {
      setImpersonateInput('u2646354@uel.ac.uk');
    }
  }, [isAdmin]);
  const [sidebarKey, setSidebarKey] = useState(0);

  const handleDeleteAllHistory = async () => {
    const apiUrl = window.__PAILA_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || '/api';
    const headers: Record<string, string> = {
      Authorization: `Bearer ${idToken}`,
    };
    if (impersonatedStudentId) {
      headers['X-Impersonate-Student-Id'] = impersonatedStudentId;
    }

    const response = await fetch(`${apiUrl}/sessions`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to delete chat history (HTTP ${response.status})`);
    }

    // Force SessionSidebar to remount and refetch
    setSidebarKey((k) => k + 1);
    navigate('/');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      <a href="#main-content" className="skip-link">{t('skipToMain')}</a>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setSidebarOpen(false); }}
        />
      )}

      {/* Sidebar */}
      <aside
        aria-label={t('sidebarNavigation')}
        className={`
          fixed lg:static inset-y-0 left-0 z-30
          w-72 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <img
                src="/uel-crest.webp"
                alt=""
                aria-hidden="true"
                className="w-10 h-10 object-contain"
              />
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-bold text-gray-900 dark:text-white">{t('appName')}</h1>
                  {BETA_MODE && <BetaPill />}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('appSubtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              aria-label={t('closeSidebar')}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* New Chat Button */}
          <div className="p-4">
            <a
              href="/"
              className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-uel-purple-600 rounded-lg hover:bg-uel-purple-700 transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t('newChat')}
            </a>
          </div>

          {/* Admin Impersonation */}
          {isAdmin && (
            <div className="px-4 pb-3">
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">
                  {t('adminViewAsStudent')}
                </p>
                {impersonatedStudentId ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-800 dark:text-amber-300 truncate">
                      {impersonatedStudentId}
                    </span>
                    <button
                      onClick={() => {
                        setImpersonation(null);
                        setImpersonateInput('');
                      }}
                      className="ml-2 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800 rounded hover:bg-amber-200 dark:hover:bg-amber-700"
                    >
                      {t('stop')}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      aria-label={t('studentEmailLabel')}
                      placeholder={t('studentEmailPlaceholder')}
                      value={impersonateInput}
                      onChange={(e) => setImpersonateInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && impersonateInput.trim()) {
                          setImpersonation(impersonateInput.trim());
                          setImpersonateInput('');
                        }
                      }}
                      className="flex-1 min-w-0 px-2 py-1 text-sm border border-amber-300 dark:border-amber-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                    <button
                      onClick={() => {
                        if (impersonateInput.trim()) {
                          setImpersonation(impersonateInput.trim());
                          setImpersonateInput('');
                        }
                      }}
                      className="px-2 py-1 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700"
                    >
                      {t('go')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Session History */}
          <div className="flex-1 overflow-y-auto">
            <SessionSidebar key={sidebarKey} />
          </div>

          {/* User Info & Settings */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-uel-gold-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="text-sm">
                  <p className="font-medium text-gray-900 dark:text-white truncate max-w-[150px]">
                    {user?.name || t('student')}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">
                    {impersonatedStudentId
                      ? `(as ${impersonatedStudentId})`
                      : user?.studentId || ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Theme Toggle */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('theme')}
              </span>
              <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setTheme('light')}
                  className={`p-1.5 rounded ${
                    theme === 'light'
                      ? 'bg-white dark:bg-gray-600 shadow'
                      : ''
                  }`}
                  title={t('lightMode')}
                  aria-label={t('lightMode')}
                  aria-pressed={theme === 'light'}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setTheme('dark')}
                  className={`p-1.5 rounded ${
                    theme === 'dark'
                      ? 'bg-white dark:bg-gray-600 shadow'
                      : ''
                  }`}
                  title={t('darkMode')}
                  aria-label={t('darkMode')}
                  aria-pressed={theme === 'dark'}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => setTheme('system')}
                  className={`p-1.5 rounded ${
                    theme === 'system'
                      ? 'bg-white dark:bg-gray-600 shadow'
                      : ''
                  }`}
                  title={t('systemTheme')}
                  aria-label={t('systemTheme')}
                  aria-pressed={theme === 'system'}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Demo Mode (dev only) — scripted answers for presentations */}
            {DEMO_MODE_AVAILABLE && (
              <div className="flex items-center justify-between mb-3" title={t('demoModeHint')}>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('demoMode')}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={demoMode}
                  aria-label={t('demoMode')}
                  onClick={() => setDemoMode(!demoMode)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors
                    focus:outline-none focus:ring-2 focus:ring-uel-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
                    ${demoMode ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform
                      ${demoMode ? 'translate-x-5' : 'translate-x-0.5'}`}
                  />
                </button>
              </div>
            )}

            {/* Language Selector */}
            <SidebarLanguageSelector />

            {/* Clear Chat History */}
            <button
              onClick={() => setShowDeleteAllModal(true)}
              className="w-full flex items-center justify-center px-4 py-2 mb-2 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              {t('clearChatHistory')}
            </button>

            {/* Sign Out */}
            <button
              onClick={signOut}
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {t('signOut')}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main id="main-content" aria-label="Chat" className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label={t('openNavMenu')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <div className="flex items-center space-x-2">
            <img
              src="/uel-crest.webp"
              alt=""
              aria-hidden="true"
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-gray-900 dark:text-white">{t('appName')}</span>
            {BETA_MODE && <BetaPill />}
          </div>
          <div className="w-10" /> {/* Spacer */}
        </header>

        {/* Impersonation warning banner */}
        {impersonatedStudentId && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
            <span className="text-sm font-medium">
              {t('viewingAs', { studentId: impersonatedStudentId })}
            </span>
            <button
              onClick={() => setImpersonation(null)}
              className="text-sm font-medium underline hover:no-underline"
            >
              {t('stopImpersonating')}
            </button>
          </div>
        )}

        {children}
      </main>

      {showDeleteAllModal && (
        <ConfirmDeleteAllModal
          onConfirm={handleDeleteAllHistory}
          onClose={() => setShowDeleteAllModal(false)}
        />
      )}
    </div>
  );
}
